import { useState, useEffect } from "react";
import { ArrowRightLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function TransferPendingConsumptionCard() {
  const { activeRestaurant } = useRestaurantContext();
  const [transferEnabled, setTransferEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const fetchSetting = async () => {
      if (!activeRestaurant) return;
      
      try {
        const { data, error } = await supabase
          .from("restaurants")
          .select("transfer_pending_consumption")
          .eq("id", activeRestaurant.id)
          .single();

        if (error) throw error;
        setTransferEnabled(data?.transfer_pending_consumption ?? false);
      } catch (error) {
        console.error("Error fetching transfer setting:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSetting();
  }, [activeRestaurant]);

  const handleToggle = async (enabled: boolean) => {
    if (!activeRestaurant) return;
    
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from("restaurants")
        .update({ transfer_pending_consumption: enabled })
        .eq("id", activeRestaurant.id);

      if (error) throw error;

      setTransferEnabled(enabled);
      toast.success(enabled 
        ? "Transferência automática ativada" 
        : "Transferência automática desativada"
      );
    } catch (error) {
      console.error("Error updating transfer setting:", error);
      toast.error("Erro ao atualizar configuração");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ArrowRightLeft className="h-5 w-5" />
          Transferir Movimento Pendente
        </CardTitle>
        <CardDescription>
          Ao fechar o período operacional, transferir automaticamente mesas e comandas 
          com consumo aberto para o próximo período.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <Label className="text-base">Habilitar transferência automática</Label>
            <p className="text-sm text-muted-foreground">
              Permite fechar o período mesmo com consumos abertos
            </p>
          </div>
          <Switch
            checked={transferEnabled}
            onCheckedChange={handleToggle}
            disabled={isLoading || isUpdating}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Quando desativado, o fechamento do período será bloqueado se houver 
          consumos não finalizados.
        </p>
      </CardContent>
    </Card>
  );
}
